import {
  WRITE_AUTHORITY_SCHEMA,
  isWriteAuthorityDomain,
  type WriteAuthorityDomain,
} from "./write-authority-domains";

export const DOMAIN_LEGACY_OWNED_CODE = "DOMAIN_LEGACY_OWNED" as const;

/**
 * Validated projection of the cross-service rejected-write contract
 * (contracts/rejected-write-error.md). Only the machine fields cross the
 * boundary; the server `message` is checked for presence and never retained
 * — the UI renders the local pointer copy.
 */
export interface DomainLegacyOwnedRejection {
  code: typeof DOMAIN_LEGACY_OWNED_CODE;
  domain: WriteAuthorityDomain;
  writeAuthority: "legacy";
  authoritySchema: typeof WRITE_AUTHORITY_SCHEMA;
}

/**
 * Recognises exactly a 409 whose body is
 * `{code:"DOMAIN_LEGACY_OWNED", message, details:{domain, write_authority:"legacy",
 * authority_schema:"write-authority.v1"}}`. Anything else (other status,
 * missing/extra-typed fields, unknown domain, other schema) is `null` so
 * ordinary conflict classification keeps its existing UX.
 */
export function parseDomainLegacyOwned(
  status: number,
  body: unknown,
): DomainLegacyOwnedRejection | null {
  if (status !== 409 || !isRecord(body)) {
    return null;
  }
  if (body.code !== DOMAIN_LEGACY_OWNED_CODE || typeof body.message !== "string") {
    return null;
  }
  const details = body.details;
  if (!isRecord(details)) {
    return null;
  }
  if (
    !isWriteAuthorityDomain(details.domain) ||
    details.write_authority !== "legacy" ||
    details.authority_schema !== WRITE_AUTHORITY_SCHEMA
  ) {
    return null;
  }
  return {
    code: DOMAIN_LEGACY_OWNED_CODE,
    domain: details.domain,
    writeAuthority: "legacy",
    authoritySchema: WRITE_AUTHORITY_SCHEMA,
  };
}

/** Typed transport error every write client throws for a recognised rejection. */
export class DomainLegacyOwnedError extends Error {
  readonly status = 409;

  constructor(readonly rejection: DomainLegacyOwnedRejection) {
    super(`The ${rejection.domain} domain is managed in the legacy application.`);
    this.name = "DomainLegacyOwnedError";
  }
}

type RejectionListener = (rejection: DomainLegacyOwnedRejection) => void;

const listeners = new Set<RejectionListener>();

/**
 * Notification bridge from the (non-React) transports to the provider's
 * global pointer host (T036 belt-and-braces). Transports publish once per
 * recognised 409; the provider subscribes for its lifetime.
 */
export function subscribeToDomainLegacyOwnedRejections(listener: RejectionListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function publishDomainLegacyOwnedRejection(rejection: DomainLegacyOwnedRejection): void {
  for (const listener of listeners) {
    listener(rejection);
  }
}

/**
 * Shared transport step: reads a 409 body once, and if it is the legacy-owned
 * contract, publishes it and returns the typed error for the caller to throw.
 * Returns `null` (with the parsed body) otherwise so the caller continues its
 * own conflict classification on the same body.
 */
export function rejectIfDomainLegacyOwned(
  status: number,
  body: unknown,
): DomainLegacyOwnedError | null {
  const rejection = parseDomainLegacyOwned(status, body);
  if (rejection === null) {
    return null;
  }
  publishDomainLegacyOwnedRejection(rejection);
  return new DomainLegacyOwnedError(rejection);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
